import React, { memo, useContext, FC } from 'react';

import { CloseButton } from '../CloseButton';
import { ToastContext } from './ToastContext';
import { ToastProps } from './Toast.types';
import { classes } from './Toast.classes';
import { WixDesignSystemContext } from '../WixDesignSystemProvider/context';
import { useHorizontalSwipe } from './hooks/useHorizontalSwipe';

const DEFAULT_ROLE: Exclude<ToastProps['role'], undefined> = 'status';

const Toast: FC<ToastProps> = memo(
  ({
    dataHook,
    id,
    role = DEFAULT_ROLE,
    className,
    dismissible,
    dismissLabel,
    onDismiss,
    children,
  }) => {
    const ariaLive = role === 'alert' ? 'assertive' : 'polite';
    const hidden = useContext(ToastContext)?.hidden ?? false;
    const { mobile } = useContext(WixDesignSystemContext);

    const swipeHandlers = useHorizontalSwipe({
      onSwipe: () => dismissible && onDismiss?.(),
      disabled: hidden || !dismissible,
    });

    const handleDismissClick = (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      onDismiss?.(event);
    };

    return (
      <div
        id={id}
        className={classes.root({ mobile }, className)}
        data-hook={dataHook}
        role={role}
        aria-live={ariaLive}
        aria-hidden={hidden}
        {...swipeHandlers}
      >
        <div data-hook="toast-content" className={classes.content()}>
          {children}
        </div>
        {dismissible && (
          <div className={classes.dismiss()}>
            <CloseButton
              className={classes.dismissButton()}
              dataHook="toast-dismiss-button"
              skin="light"
              size="medium"
              aria-label={dismissLabel}
              disabled={hidden}
              onClick={handleDismissClick}
            />
          </div>
        )}
      </div>
    );
  },
);

Toast.displayName = 'Toast';

export { Toast };
